import React, { createContext, useContext, useEffect, useState } from 'react';
import { getApiBaseUrl, getToken, saveToken, removeToken } from '../api/utils';

const AuthContext = createContext(null);
const API_URL = getApiBaseUrl();
const ADMIN_EMAILS = new Set(["luxmeera@gmail.com"]);

export const useAuth = () => useContext(AuthContext);

function normalizeUser(currentUser, token) {
  const normalizedEmail = (currentUser?.email || "").trim().toLowerCase();
  const isAdmin =
    Boolean(currentUser?.is_admin) ||
    (normalizedEmail ? ADMIN_EMAILS.has(normalizedEmail) : false);

  return {
    ...currentUser,
    token,
    is_admin: isAdmin,
    isAuthenticated: true,
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  const fetchCurrentUser = async (token) => {
    if (!token) {
      return null;
    }

    const response = await fetch(`${API_URL}/users/users/me/`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch current user (${response.status})`);
    }

    const data = await response.json();
    const currentUser = data?.user || data;

    if (!currentUser) {
      throw new Error("Current user payload was empty");
    }

    return normalizeUser(currentUser, token);
  };

  useEffect(() => {
    let isMounted = true;

    const restoreSession = async () => {
      const token = getToken();
      if (!token) {
        if (isMounted) {
          setUser(null);
          setIsAuthLoading(false);
        }
        return;
      }

      try {
        const currentUser = await fetchCurrentUser(token);
        if (isMounted) {
          setUser(currentUser);
        }
      } catch (error) {
        removeToken();
        if (isMounted) {
          setUser(null);
        }
      } finally {
        if (isMounted) {
          setIsAuthLoading(false);
        }
      }
    };

    restoreSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (token) => {
    saveToken(token);
    try {
      const currentUser = await fetchCurrentUser(token);
      setUser(currentUser);
      setIsAuthLoading(false);
      return currentUser;
    } catch (error) {
      removeToken();
      setUser(null);
      setIsAuthLoading(false);
      throw error;
    }
  };

  const logout = () => {
    removeToken();
    setUser(null);
    setIsAuthLoading(false);
  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    isAuthLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
